import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, setToken, setRole } from '../lib/api';
import { BRAND, COUNTRY_CODES } from '../mock/mock';
import { useToast } from '../hooks/use-toast';
import StudentDrawer from '../components/recruiter/StudentDrawer';
import RecruiterMessagesTab from '../components/recruiter/RecruiterMessagesTab';
import ChangePasswordModal from '../components/common/ChangePasswordModal';
import {
  LogOut, Users, Plus, MessageSquare, Search, RefreshCw, FileText, Upload, Trash2,
  CheckCircle2, Loader2, Save, Eye, Building2, GraduationCap, X, Clock, Mail, Phone, KeyRound,
} from 'lucide-react';

const TABS = [
  { key: 'students', label: 'My Students', icon: Users },
  { key: 'create', label: 'Create Student', icon: Plus },
  { key: 'messages', label: 'Messages', icon: MessageSquare },
];

const stagePill = (v) => {
  const map = {
    draft: ['bg-[#eee]', 'text-[#4b5e4d]', 'Draft'],
    submitted: ['bg-[#FEF4DB]', 'text-[#8a5e00]', 'Submitted'],
    applied: ['bg-[#DEE9FA]', 'text-[#1e40af]', 'Applied'],
    admitted: ['bg-[#E1F3E4]', 'text-[#0B5E2A]', 'Admitted'],
    enrolled: ['bg-[#E1F3E4]', 'text-[#0B5E2A]', 'Enrolled'],
    rejected: ['bg-[#FDE2E2]', 'text-[#9b1c1c]', 'Rejected'],
  };
  const [bg, tx, label] = map[v] || map.submitted;
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${bg} ${tx}`}>{label}</span>;
};

const DRAFT_KEY = 'hcc_student_draft';

const emptyForm = {
  full_name: '', email: '', phone_code: '+1', phone: '',
  dob: '', gender: '', nationality: '', address: '',
  passport_pr_status: '', preferred_college_id: '', preferred_program: '',
  preferred_intake: '', needs_exam_prep: false, exam_prep_types: [],
  notes: '',
};

const RECOMMENDED_DOCS = [
  { key: 'passport', label: 'Passport / ID', hint: 'Passport, driving license, or national ID', required: true, multiple: false },
  { key: 'marksheet_12', label: '12th Marksheet', hint: 'Senior-secondary / high school marksheet', multiple: false },
  { key: 'marksheet_10', label: '10th Marksheet', hint: 'Secondary school marksheet', multiple: false },
  { key: 'ielts', label: 'English Test Score', hint: 'IELTS / TOEFL / PTE / Duolingo', multiple: false },
  { key: 'transcripts', label: 'Degree Transcripts', hint: 'Bachelors / Masters transcripts', multiple: true },
  { key: 'sop', label: 'Statement of Purpose', hint: 'SOP / personal statement', multiple: false },
  { key: 'resume', label: 'Resume / CV', multiple: false },
  { key: 'other', label: 'Other Documents', hint: 'Work experience, certificates, etc.', multiple: true },
];

const RecruiterDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState('students');
  const [me, setMe] = useState(null);
  const [students, setStudents] = useState([]);
  const [options, setOptions] = useState(null);
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [showChangePw, setShowChangePw] = useState(false);
  const [unreadMsg, setUnreadMsg] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meR, sR, oR, cR] = await Promise.all([
        api.get('/recruiter/me'),
        api.get('/recruiter/students'),
        api.get('/recruiter/options'),
        api.get('/recruiter/colleges'),
      ]);
      setMe(meR.data); setStudents(sR.data); setOptions(oR.data); setColleges(cR.data);
    } catch (e) { /* 401 global */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll unread count (reset when switching to Messages tab, since RecruiterMessagesTab marks read)
  useEffect(() => {
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get('/recruiter/messages/unread-count');
        if (!cancelled) setUnreadMsg(data?.unread || 0);
      } catch {}
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [tab]);

  const logout = () => { setToken(null); setRole(null); navigate('/recruiter/login'); };

  const q = filter.toLowerCase();
  const filtered = students.filter((s) =>
    !q || (s.full_name || '').toLowerCase().includes(q) ||
    (s.email || '').toLowerCase().includes(q) ||
    (s.nationality || '').toLowerCase().includes(q)
  );

  return (
    <main className="min-h-screen bg-[#f6faf3]">
      <header className="bg-white border-b border-[#0B5E2A]/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto container-px h-[64px] flex items-center justify-between">
          <Link to="/recruiter" className="flex items-center gap-3">
            <img src={BRAND.logo} alt="HCC" className="h-9 w-9 rounded-full" />
            <div className="leading-tight">
              <div className="font-display text-[15px] font-bold brand-green">Recruiter Portal</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-brand-gold font-semibold">{me?.company || 'HCC Admissions Partner'}</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <div className="text-sm font-semibold text-[#0B5E2A]">{me?.name || ''}</div>
              <div className="text-xs text-[#4b5e4d]">
                {me?.commission_per_student ? `$${Number(me.commission_per_student).toLocaleString()} / student` : 'No commission set'}
              </div>
            </div>
            <button onClick={() => setShowChangePw(true)} data-testid="recruiter-change-password-btn" className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#0B5E2A]/20 text-sm text-[#0B5E2A] hover:bg-[#f0f6ee] transition-colors">
              <KeyRound className="h-4 w-4" /> Change Password
            </button>
            <button onClick={logout} data-testid="recruiter-logout-btn" className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#0B5E2A]/20 text-sm text-[#0B5E2A] hover:bg-[#f0f6ee] transition-colors">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto container-px py-6">
        {/* Tabs */}
        <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
          <div className="inline-flex bg-white border border-[#0B5E2A]/10 rounded-lg p-1">
            {TABS.map((t) => {
              const Icon = t.icon; const active = tab === t.key;
              const showBadge = t.key === 'messages' && unreadMsg > 0;
              return (
                <button key={t.key} data-testid={`recruiter-nav-${t.key}`} onClick={() => setTab(t.key)} className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${active ? 'bg-[#0B5E2A] text-white' : 'text-[#4b5e4d] hover:text-[#0B5E2A]'}`}>
                  <Icon className="h-4 w-4" /> {t.label} {t.key === 'students' && <span className={`text-xs ${active ? 'text-white/80' : 'text-[#6b7e6d]'}`}>({students.length})</span>}
                  {showBadge && (<span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-[#F0B93C] text-[#06401d] text-[11px] font-bold">{unreadMsg}</span>)}
                </button>
              );
            })}
          </div>
          {tab === 'students' && (
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6b7e6d]" />
                <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search students..." className="pl-9 pr-3 py-2 w-64 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30 bg-white" />
              </div>
              <button onClick={load} className="h-9 w-9 rounded-md border border-[#0B5E2A]/20 text-[#0B5E2A] hover:bg-[#f0f6ee] flex items-center justify-center transition-colors" title="Refresh">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          )}
        </div>

        {/* STUDENTS tab */}
        {tab === 'students' && (
          <>
            {/* Commission summary */}
            {students.length > 0 && (() => {
              const assigned = students.filter((s) => s.assigned_college && s.application_stage !== 'rejected');
              const paid = students.filter((s) => s.application_stage === 'enrolled').reduce((a, s) => a + Number(s.recruiter_commission_snapshot || 0), 0);
              const pending = assigned.filter((s) => s.application_stage !== 'enrolled').reduce((a, s) => a + Number(s.recruiter_commission_snapshot || 0), 0);
              const totalEarned = paid + pending;
              const card = (label, value, sub, accent) => (
                <div className={`rounded-xl p-4 border ${accent === 'gold' ? 'bg-[#FEF8E6] border-[#F0B93C]/40' : accent === 'green' ? 'bg-[#f5f9f3] border-[#0B5E2A]/20' : 'bg-white border-[#0B5E2A]/10'}`}>
                  <div className="text-[10px] uppercase tracking-wider text-[#6b7e6d] font-semibold">{label}</div>
                  <div className="mt-1 font-display text-2xl font-bold text-[#0B5E2A]">${Number(value).toLocaleString()}</div>
                  <div className="text-[11px] text-[#6b7e6d] mt-0.5">{sub}</div>
                </div>
              );
              return (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                  {card('Total Earned', totalEarned, `${assigned.length} assigned student${assigned.length === 1 ? '' : 's'}`, null)}
                  {card('Paid', paid, 'Students enrolled', 'green')}
                  {card('Pending', pending, 'Unlocked when students enroll', 'gold')}
                </div>
              );
            })()}

          <div className="bg-white border border-[#0B5E2A]/10 rounded-xl overflow-hidden">
            {loading ? (
              <div className="py-16 text-center text-[#6b7e6d]"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
            ) : filtered.length === 0 ? (
              <div className="py-16 text-center">
                <Users className="h-10 w-10 mx-auto brand-green opacity-60" />
                <div className="mt-3 font-display text-lg text-[#0B5E2A]">No students yet</div>
                <p className="mt-1 text-sm text-[#6b7e6d]">Create your first student profile to start submitting applications.</p>
                <button onClick={() => setTab('create')} className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#0B5E2A] text-white font-semibold text-sm hover:bg-[#094a22] transition-colors">
                  <Plus className="h-4 w-4" /> Create Student
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#f0f6ee] text-[#0B5E2A]">
                    <tr>
                      <Th>Student</Th><Th>Nationality</Th><Th>Assigned College / Program</Th><Th>Commission</Th><Th>Stage</Th><Th>Submitted</Th><Th></Th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((s) => {
                      const commission = Number(s.recruiter_commission_snapshot || 0);
                      const defaultComm = Number(me?.commission_per_student || 0);
                      const showComm = s.assigned_college ? commission : defaultComm;
                      const isEnrolled = s.application_stage === 'enrolled';
                      return (
                        <tr key={s.id} className="border-t border-[#0B5E2A]/10 hover:bg-[#faf8ee] cursor-pointer" onClick={() => setSelected(s)}>
                          <Td>
                            <div className="font-semibold text-[#0B5E2A]">{s.full_name}</div>
                            <div className="text-xs text-[#6b7e6d]">{s.email}</div>
                          </Td>
                          <Td>{s.nationality}</Td>
                          <Td>
                            {s.assigned_college_name ? (
                              <div>
                                <div className="font-medium flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 brand-green" />{s.assigned_college_name}</div>
                                {s.assigned_program_name && <div className="text-xs text-[#6b7e6d] flex items-center gap-1.5 mt-0.5"><GraduationCap className="h-3 w-3" />{s.assigned_program_name}</div>}
                              </div>
                            ) : (<span className="text-xs text-[#6b7e6d] italic">Not assigned</span>)}
                          </Td>
                          <Td>
                            {showComm > 0 ? (
                              <div>
                                <div className={`inline-flex items-center gap-1 font-bold ${isEnrolled ? 'text-[#0B5E2A]' : 'text-[#1b2a1f]'}`} data-testid={`student-commission-${s.id}`}>
                                  ${showComm.toLocaleString()}
                                </div>
                                <div className="text-[10px] text-[#6b7e6d]">
                                  {s.assigned_college ? (isEnrolled ? 'Paid on enrollment' : 'On enrollment') : 'Default rate'}
                                </div>
                              </div>
                            ) : (<span className="text-xs text-[#6b7e6d] italic">—</span>)}
                          </Td>
                          <Td>{stagePill(s.application_stage)}</Td>
                          <Td className="text-xs text-[#6b7e6d]">{new Date(s.created_at).toLocaleDateString()}</Td>
                          <Td className="text-right pr-4">
                            <button onClick={(e) => { e.stopPropagation(); setSelected(s); }} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded border border-[#0B5E2A]/25 text-[#0B5E2A] hover:bg-[#f0f6ee] transition-colors">
                              <Eye className="h-3.5 w-3.5" /> View
                            </button>
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </>
        )}

        {/* CREATE STUDENT tab */}
        {tab === 'create' && (
          <CreateStudentForm options={options} colleges={colleges} onCreated={() => { setTab('students'); load(); }} />
        )}

        {/* MESSAGES tab */}
        {tab === 'messages' && (
          <RecruiterMessagesTab me={me} />
        )}
      </div>

      {selected && (
        <StudentDrawer student={selected} onClose={() => setSelected(null)} onRefresh={load} />
      )}
      {showChangePw && (
        <ChangePasswordModal role="recruiter" onClose={() => setShowChangePw(false)} />
      )}
    </main>
  );
};

const Th = ({ children, className = '' }) => <th className={`text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider ${className}`}>{children}</th>;
const Td = ({ children, className = '' }) => <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;

// ================ Create Student Form ================

const CreateStudentForm = ({ options, colleges, onCreated }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(() => {
    try { return { ...emptyForm, ...(JSON.parse(localStorage.getItem(DRAFT_KEY) || '{}')) }; }
    catch { return emptyForm; }
  });
  const [files, setFiles] = useState([]); // [{file, doc_type}]
  const [verified, setVerified] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const formRef = useRef(form);
  formRef.current = form;

  // Auto-save draft every 30s + beforeunload
  useEffect(() => {
    const saveDraft = () => {
      try { localStorage.setItem(DRAFT_KEY, JSON.stringify(formRef.current)); setDraftSavedAt(new Date()); } catch {}
    };
    const iv = setInterval(saveDraft, 30000);
    window.addEventListener('beforeunload', saveDraft);
    return () => { clearInterval(iv); window.removeEventListener('beforeunload', saveDraft); saveDraft(); };
  }, []);

  const onChange = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const toggleExam = (t) => {
    const curr = form.exam_prep_types || [];
    setForm({ ...form, exam_prep_types: curr.includes(t) ? curr.filter((x) => x !== t) : [...curr, t] });
  };

  const onFilesForCategory = (doc_type, e) => {
    const newFiles = Array.from(e.target.files || []).map((f) => ({ file: f, doc_type }));
    if (!newFiles.length) return;
    const category = RECOMMENDED_DOCS.find((d) => d.key === doc_type);
    // Single-file categories: replace any existing file in that category.
    const others = category && !category.multiple
      ? files.filter((f) => f.doc_type !== doc_type)
      : files;
    setFiles([...others, ...newFiles]);
    // Reset input so the same filename can be re-selected.
    e.target.value = '';
  };

  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const selectedCollege = colleges.find((c) => c.id === form.preferred_college_id);

  const submit = async (e) => {
    e.preventDefault();
    const required = ['full_name', 'email', 'phone', 'dob', 'nationality', 'passport_pr_status'];
    const missing = required.filter((k) => !form[k]);
    if (missing.length) {
      toast({ title: 'Missing required fields', description: missing.join(', ') });
      return;
    }
    if (files.length === 0) {
      toast({ title: 'Documents required', description: 'Please attach at least the passport / ID.' });
      return;
    }
    const hasPassport = files.some((f) => f.doc_type === 'passport');
    if (!hasPassport) {
      toast({ title: 'Passport / ID required', description: 'Please attach a passport, driving license, or national ID.' });
      return;
    }
    if (!verified) {
      toast({ title: 'Verification required', description: 'Please confirm the acknowledgement at the bottom of the form.' });
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('full_name', form.full_name);
      fd.append('email', form.email);
      fd.append('phone_code', form.phone_code);
      fd.append('phone', form.phone);
      fd.append('dob', form.dob);
      fd.append('gender', form.gender || '');
      fd.append('nationality', form.nationality);
      fd.append('address', form.address || '');
      fd.append('passport_pr_status', form.passport_pr_status);
      fd.append('preferred_program', form.preferred_program || '');
      fd.append('preferred_intake', form.preferred_intake || '');
      fd.append('needs_exam_prep', form.needs_exam_prep ? 'true' : 'false');
      fd.append('exam_prep_types', (form.exam_prep_types || []).join(','));
      fd.append('notes', form.notes || '');
      const dtypes = [];
      files.forEach((f) => { fd.append('documents', f.file); dtypes.push(f.doc_type); });
      fd.append('doc_types', dtypes.join(','));
      fd.append('verified', 'true');
      await api.post('/recruiter/students', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      localStorage.removeItem(DRAFT_KEY);
      toast({ title: 'Student created', description: `${form.full_name} added to your pipeline.` });
      onCreated?.();
    } catch (err) {
      toast({ title: 'Create failed', description: err?.response?.data?.detail || 'Error' });
    } finally { setBusy(false); }
  };

  const clearDraft = () => {
    if (!window.confirm('Clear all fields and delete the saved draft?')) return;
    setForm(emptyForm); setFiles([]); localStorage.removeItem(DRAFT_KEY); setDraftSavedAt(null);
  };

  return (
    <div className="bg-white border border-[#0B5E2A]/10 rounded-xl p-6 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl text-[#0B5E2A]">Create Student Profile</h2>
          <p className="text-sm text-[#6b7e6d] mt-1">Fill in the student's details and attach required documents.</p>
        </div>
        <div className="text-right">
          {draftSavedAt && <div className="text-xs text-[#6b7e6d] flex items-center gap-1.5"><Save className="h-3 w-3" /> Draft saved {draftSavedAt.toLocaleTimeString()}</div>}
          <button type="button" onClick={clearDraft} className="mt-1 text-xs text-[#9b1c1c] hover:underline">Clear draft</button>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <Section title="Personal Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Full Name *" value={form.full_name} onChange={onChange('full_name')} />
            <Field label="Email *" value={form.email} onChange={onChange('email')} type="email" />
            <div>
              <label className="block text-sm font-medium text-[#2e4030] mb-1.5">Phone *</label>
              <div className="flex gap-2">
                <select value={form.phone_code} onChange={onChange('phone_code')} className="w-[130px] px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30">
                  {COUNTRY_CODES.map((c) => <option key={c.label} value={c.code}>{c.label}</option>)}
                </select>
                <input value={form.phone} onChange={onChange('phone')} className="flex-1 px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30" />
              </div>
            </div>
            <Field label="Date of Birth *" value={form.dob} onChange={onChange('dob')} type="date" />
            <Select label="Gender" value={form.gender} onChange={onChange('gender')} options={options?.genders || []} placeholder="Select..." />
            <Field label="Nationality *" value={form.nationality} onChange={onChange('nationality')} />
            <Field full label="Address" value={form.address} onChange={onChange('address')} />
            <Select full label="Status in Canada *" value={form.passport_pr_status} onChange={onChange('passport_pr_status')} options={options?.passport_pr_status || []} placeholder="Select status..." />
          </div>
        </Section>

        <Section title="Preferences">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2e4030] mb-1.5">Preferred College</label>
              <select value={form.preferred_college_id} onChange={(e) => { const id = e.target.value; setForm({ ...form, preferred_college_id: id, preferred_program: '' }); }} className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30">
                <option value="">Select a college...</option>
                {colleges.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.location}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2e4030] mb-1.5">Preferred Program</label>
              <select value={form.preferred_program} onChange={onChange('preferred_program')} disabled={!selectedCollege} className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30 disabled:bg-[#f6faf3] disabled:text-[#6b7e6d]">
                <option value="">{selectedCollege ? 'Select a program...' : 'Choose college first'}</option>
                {(selectedCollege?.programs || []).map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </div>
            <Field label="Preferred Intake" value={form.preferred_intake} onChange={onChange('preferred_intake')} placeholder="e.g. Fall 2026" />
            <div />
            <div className="md:col-span-2">
              <label className="inline-flex items-center gap-2 text-sm text-[#2e4030]">
                <input type="checkbox" checked={form.needs_exam_prep} onChange={onChange('needs_exam_prep')} className="accent-[#0B5E2A] h-4 w-4" />
                <span className="font-medium">Student needs exam preparation</span>
              </label>
              {form.needs_exam_prep && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {(options?.exam_prep_types || []).map((t) => {
                    const sel = form.exam_prep_types?.includes(t);
                    return (
                      <button key={t} type="button" onClick={() => toggleExam(t)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${sel ? 'bg-[#0B5E2A] text-white border-[#0B5E2A]' : 'bg-white text-[#4b5e4d] border-[#0B5E2A]/20 hover:border-[#0B5E2A]'}`}>
                        {t}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#2e4030] mb-1.5">Notes</label>
              <textarea value={form.notes} onChange={onChange('notes')} rows={3} className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30 resize-y" />
            </div>
          </div>
        </Section>

        <Section title={`Documents (${files.length})`}>
          <p className="text-xs text-[#6b7e6d] -mt-2 mb-3">Attach files for each category. PDFs, images, or docs up to 10MB each.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {RECOMMENDED_DOCS.map((cat) => {
              const attached = files
                .map((f, idx) => ({ ...f, _idx: idx }))
                .filter((f) => f.doc_type === cat.key);
              const hasFiles = attached.length > 0;
              return (
                <div
                  key={cat.key}
                  data-testid={`doc-category-${cat.key}`}
                  className={`border rounded-xl p-4 transition-colors ${hasFiles ? 'border-[#0B5E2A]/40 bg-[#f5f9f3]' : 'border-[#0B5E2A]/15 bg-white'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-sm font-semibold text-[#0B5E2A]">
                        {cat.label}
                        {cat.required && <span className="text-[10px] font-bold uppercase text-[#9b1c1c] bg-[#FDE2E2] rounded px-1.5 py-0.5">Required</span>}
                      </div>
                      {cat.hint && <div className="text-[11px] text-[#6b7e6d] mt-0.5">{cat.hint}</div>}
                    </div>
                    <label
                      data-testid={`doc-upload-${cat.key}`}
                      className="shrink-0 cursor-pointer inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md border border-[#0B5E2A]/25 text-[#0B5E2A] hover:bg-[#edf6ea] transition-colors"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      {hasFiles ? (cat.multiple ? 'Add' : 'Replace') : 'Upload'}
                      <input
                        type="file"
                        className="hidden"
                        multiple={cat.multiple}
                        onChange={(e) => onFilesForCategory(cat.key, e)}
                      />
                    </label>
                  </div>

                  {hasFiles ? (
                    <ul className="mt-3 space-y-1.5">
                      {attached.map((f) => (
                        <li key={f._idx} className="flex items-center gap-2 bg-white border border-[#0B5E2A]/10 rounded-md px-2.5 py-1.5">
                          <FileText className="h-3.5 w-3.5 brand-green shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="truncate text-xs font-medium">{f.file.name}</div>
                            <div className="text-[10px] text-[#6b7e6d]">{Math.ceil(f.file.size / 1024)} KB</div>
                          </div>
                          <button
                            type="button"
                            data-testid={`doc-remove-${cat.key}-${f._idx}`}
                            onClick={() => removeFile(f._idx)}
                            className="h-6 w-6 rounded text-[#9b1c1c] hover:bg-[#FDE2E2] flex items-center justify-center"
                            title="Remove"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="mt-3 text-[11px] text-[#6b7e6d] italic">
                      {cat.required ? 'No file attached yet.' : 'Optional'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Section>

        <div className="rounded-xl border border-[#F0B93C]/40 bg-[#FEF8E6] p-4" data-testid="student-verification-ack-box">
          <div className="text-xs font-semibold text-[#8a5e00] uppercase tracking-wider mb-2">Verification Acknowledgement</div>
          <label className="flex items-start gap-2 text-sm text-[#2e4030]">
            <input
              type="checkbox"
              checked={verified}
              onChange={(e) => setVerified(e.target.checked)}
              data-testid="student-verification-ack"
              className="mt-1 accent-[#0B5E2A]"
            />
            <span>I confirm that I have personally verified all the documents of the student above, and that the information provided is accurate and complete.</span>
          </label>
        </div>

        <div className="flex gap-2 pt-4 border-t border-[#0B5E2A]/10">
          <button type="button" onClick={clearDraft} className="px-5 py-2.5 rounded-md border border-[#0B5E2A]/25 text-[#0B5E2A] font-semibold hover:bg-[#f0f6ee] transition-colors">Reset</button>
          <button type="submit" disabled={busy || !verified} data-testid="student-submit-btn" className="flex-1 btn-green px-5 py-2.5 rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? (<><Loader2 className="h-4 w-4 animate-spin" /> Creating...</>) : (<><CheckCircle2 className="h-4 w-4 pointer-events-none" /> Submit Student</>)}
          </button>
        </div>
      </form>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h3 className="font-display text-lg text-[#0B5E2A] mb-3 pb-2 border-b border-[#0B5E2A]/10">{title}</h3>
    {children}
  </div>
);

const Field = ({ label, value, onChange, type = 'text', placeholder, full }) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <label className="block text-sm font-medium text-[#2e4030] mb-1.5">{label}</label>
    <input type={type} value={value || ''} onChange={onChange} placeholder={placeholder} className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30" />
  </div>
);

const Select = ({ label, value, onChange, options, placeholder, full }) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <label className="block text-sm font-medium text-[#2e4030] mb-1.5">{label}</label>
    <select value={value || ''} onChange={onChange} className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30">
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

export default RecruiterDashboard;
